import { IdentityRole } from "core";
import { useMemo } from "react";
import { purgeNoanAsyncStorage } from "../asyncStoragePersister";
import { useAuthContext } from "../auth/useAuthContext";
import {
  isAssetPage,
  isGuestOnboardingPage,
  isOnboardingPage,
  isWorkspacePage,
} from "../components/Layout/layoutConfig";
import { queryClient } from "../queryClient";
import { trpc } from "./trpc";

export function useProjects() {
  const {
    data,
    isPending: isLoadingProjects,
    ...rest
  } = trpc.organization.getAll.useQuery(undefined);

  return {
    projects: data ?? [],
    isLoadingProjects,
    ...rest,
  };
}

function useProjectById(id: string = "") {
  const { user } = useAuthContext();
  const { data, ...rest } = trpc.organization.getById.useQuery(
    { id },
    { enabled: !!id },
  );

  const processedIdentities = useMemo(() => {
    if (!data?.identities) return null;

    return data.identities.reduce(
      (acc, identity) => {
        if (identity.authUserId === user.id) {
          acc.currentUserIdentity = identity;
        }

        switch (identity.primaryRole) {
          case IdentityRole.Advisor:
            acc.advisors.push(identity);
            break;
          case IdentityRole.Guest:
            // Guest roles are excluded from members
            break;
          case IdentityRole.Bot:
            acc.bots.push(identity);
            break;
          default:
            // All other roles go to members
            acc.members.push(identity);
            break;
        }

        return acc;
      },
      {
        currentUserIdentity: undefined as
          | (typeof data.identities)[0]
          | undefined,
        members: [] as typeof data.identities,
        advisors: [] as typeof data.identities,
        bots: [] as typeof data.identities,
      },
    );
  }, [data, user.id]);

  return {
    project:
      data && processedIdentities
        ? {
            ...data,
            ...processedIdentities,
          }
        : undefined,
    ...rest,
  };
}

export function useProject() {
  const { identity } = useAuthContext();
  const {
    project,
    isPending: isLoadingProject,
    ...proj
  } = useProjectById(identity?.organizationId);

  return {
    ...proj,
    isLoadingProject,
    project,
  };
}

export function useProjectExternalBySlug(slug: string = "") {
  const {
    data,
    isPending: isLoadingProject,
    ...rest
  } = trpc.organization.getExternalBySlug.useQuery(
    { slug },
    { enabled: !!slug },
  );

  return {
    project: data,
    isLoadingProject,
    ...rest,
  };
}

export function useCreateProject() {
  const utils = trpc.useUtils();

  const { mutate: createProject, isPending: isCreatingProject } =
    trpc.organization.create.useMutation({
      onSettled() {
        utils.organization.getAll.invalidate();
        utils.account.planUsage.invalidate();
      },
    });

  return {
    createProject,
    isCreatingProject,
  };
}

export function useUpdateProject() {
  const utils = trpc.useUtils();

  const {
    mutate: updateProject,
    isPending: isUpdatingProject,
    status: updateProjectStatus,
    data,
  } = trpc.organization.update.useMutation({
    onSettled() {
      utils.organization.getById.invalidate();
      utils.organization.getAll.invalidate();
    },
  });

  return {
    updateProject,
    updateProjectStatus,
    isUpdatingProject,
    data,
  };
}

export function useSwitchProject() {
  const { isPending: isSwitchingProject, ...mutation } =
    trpc.organization.switch.useMutation();

  function switchProject({
    id,
    onSuccess,
  }: {
    id: string;
    onSuccess?: () => void;
  }) {
    mutation.mutate(
      { id },
      {
        onSuccess() {
          queryClient.clear();
          purgeNoanAsyncStorage();
          onSuccess?.();
        },
        onSettled() {
          location.reload();
          /**
           * If we're on a specific chat thread, we want to redirect to the
           * workspace page after switching projects as chat threads are
           * project-specific.
           */
          if (isWorkspacePage(location.pathname)) {
            location.href = "/workspace";
          }
          if (isAssetPage(location.pathname)) {
            location.href = "/create";
          }
          if (
            isOnboardingPage(location.pathname) ||
            isGuestOnboardingPage(location.pathname)
          ) {
            location.href = "/";
          }
        },
      },
    );
  }

  return {
    switchProject,
    isSwitchingProject,
  };
}

export function useProjectStacks() {
  const { project, isLoadingProject, isRefetching } = useProject();

  const projectStacks = useMemo(
    () => project?.settings.stacks || [],
    [project?.settings.stacks],
  );

  return {
    projectStacks,
    isLoadingProjectStacks: isLoadingProject,
    isRefetchingProjectStacks: isRefetching,
  };
}

export function useUpdateStacks() {
  const utils = trpc.useUtils();

  const { mutate: updateStacks, isPending: isUpdatingStacks } =
    trpc.organization.updateStacks.useMutation({
      onSettled() {
        utils.organization.getById.invalidate();
        utils.facts.getAll.invalidate();
        utils.facts.getStacksTree.invalidate();
      },
    });

  return {
    updateStacks,
    isUpdatingStacks,
  };
}

export function useLogoUpload() {
  const utils = trpc.useUtils();

  const {
    data: logoUploadURL,
    mutate: createLogoUploadURL,
    isPending: isCreatingLogoUploadURL,
    isError: isLogoUploadURLError,
  } = trpc.organization.createLogoUploadURL.useMutation();

  const {
    mutate: commitLogo,
    isPending: isCommittingLogo,
    isError: isCommitLogoError,
    data: committedLogo,
  } = trpc.organization.commitLogo.useMutation({
    onSettled() {
      utils.organization.getById.invalidate();
      utils.organization.getAll.invalidate();
    },
  });

  return {
    logoUploadURL,
    createLogoUploadURL,
    isCreatingLogoUploadURL,
    isLogoUploadURLError,

    commitLogo,
    isCommittingLogo,
    isCommitLogoError,
    committedLogo,
  };
}
export function useDeleteLogo() {
  const utils = trpc.useUtils();

  const { mutate: deleteLogo, isPending: isDeletingLogo } =
    trpc.organization.deleteLogo.useMutation({
      onSettled() {
        utils.organization.getById.invalidate();
      },
    });

  return {
    deleteLogo,
    isDeletingLogo,
  };
}

export function useClaimCoupon() {
  const utils = trpc.useUtils();

  return trpc.organization.claimCoupon.useMutation({
    onSettled() {
      utils.organization.getById.invalidate();
    },
  });
}
